import { TFunction } from 'i18next';
import { CSSProperties } from 'react';

// Discriminator constants to eliminate magic strings
export const FieldTypeKeys = {
  FORM: 'form',
  USER_IDS: 'userIds',
  FORM_VERSION: 'formVersion',
  FLAT_FILE_INTEGRATION: 'formflatFileIntegration',
  DEFAULT: 'default',
  EXTERNAL_CODE: 'externalCode',
} as const;

export type FieldType = (typeof FieldTypeKeys)[keyof typeof FieldTypeKeys];

/** Recursive tree node structure */
export interface TreeNode {
  title: string;
  value: string;
  children?: TreeNode[];
}

/** Union of possible configuration field names */
export type ConfigurationFieldName =
  (typeof FieldTypeKeys)[keyof typeof FieldTypeKeys];

/** Configuration for a form field */
export interface FormConfigurationField {
  name: ConfigurationFieldName;
  label: string;
  placeholder?: string;
  style?: CSSProperties;
  options?: { value: string; label: string }[];
  onSelect?: (value: string) => void;
}

/** Props for configuration form input (kept for compatibility) */
export interface ConfigurationFormInputProps {
  field: FormConfigurationField;
  userTreeData: TreeNode[];
  formName: string;
  setFormName: (value: string) => void;
  onEditForm: () => void;
  processForm?: { form_id?: string } | null;
  t: TFunction;
  editButtonClassName?: string;
  settingsFieldMaxWidth: number;
  formVersionMaxLength: number;
  value?: any;
  onChange?: (value: any, option?: any) => void;
}

/** Discriminated union for field values */
export type FieldValue =
  | { type: typeof FieldTypeKeys.FORM; value: string }
  | { type: typeof FieldTypeKeys.USER_IDS; value: string[] }
  | { type: typeof FieldTypeKeys.FORM_VERSION; value: string }
  | { type: typeof FieldTypeKeys.FLAT_FILE_INTEGRATION; value: string }
  | { type: typeof FieldTypeKeys.DEFAULT; value: string };

/** Discriminated union for field change handlers */
export type FieldChangeHandler =
  | {
      type: typeof FieldTypeKeys.FORM;
      handler: (value: string, option?: any) => void;
    }
  | {
      type: typeof FieldTypeKeys.USER_IDS;
      handler: (value: string[], option?: any) => void;
    }
  | {
      type: typeof FieldTypeKeys.FORM_VERSION;
      handler: (value: string) => void;
    }
  | {
      type: typeof FieldTypeKeys.FLAT_FILE_INTEGRATION;
      handler: (value: string, option?: any) => void;
    }
  | {
      type: typeof FieldTypeKeys.DEFAULT;
      handler: (value: string, option?: any) => void;
    };

/** Base props for input components */
export interface BaseInputProps {
  field: FormConfigurationField;
  value?: any;
  onChange?: (value: any, option?: any) => void;
  t: TFunction;
}

/** Props for user IDs input */
export interface UserIdsInputProps extends BaseInputProps {
  userTreeData: TreeNode[];
}

/** Props for form version input */
export interface FormVersionInputProps extends BaseInputProps {
  formName: string;
  setFormName: (value: string) => void;
  onEditForm: () => void;
  processForm?: { form_id?: string } | null;
  editButtonClassName?: string;
  settingsFieldMaxWidth: number;
  formVersionMaxLength: number;
}

/** Props for default select input */
export interface DefaultSelectProps extends BaseInputProps {}
